const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/* ZEYRAVO product feature boxes: lazy-enhance only cards near the viewport. */
(() => {
  const STYLE_ID = 'zeyravo-product-feature-boxes-style';
  const CARD_SELECTOR = '.card-wrapper';
  const PRODUCT_TITLE_SELECTOR = '.product__title h1, .product__title';
  const prefetched = new Set();
  let cardObserver;

  function addStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .zeyravo-product-features { display:flex; flex-wrap:wrap; gap:8px; margin:8px 0 6px; }
      .zeyravo-product-feature { display:inline-flex; align-items:center; min-height:32px; padding:7px 11px; border:1px solid rgba(var(--color-foreground),.38); background:transparent; color:rgba(var(--color-foreground),.9); font-size:1.25rem; font-weight:500; line-height:1; letter-spacing:.025em; text-transform:uppercase; white-space:nowrap; }
      .zeyravo-product-page-features { margin-top:10px; margin-bottom:12px; }
      @media screen and (max-width:749px) {
        .zeyravo-product-features { gap:6px; margin-top:7px; }
        .zeyravo-product-feature { min-height:29px; padding:6px 9px; font-size:1.1rem; }
        .zeyravo-product-page-features { margin-top:8px; margin-bottom:10px; }
      }
    `;
    document.head.appendChild(style);
  }

  function cleanText(html) {
    const box = document.createElement('div');
    box.innerHTML = html || '';
    return (box.textContent || box.innerText || '').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  }

  function extractFeatures(description) {
    const text = cleanText(description);
    if (!text) return [];
    const features = [];
    const materialMatch = text.match(/(\d+(?:\.\d+)?)\s*%\s*(cotton|polyester|nylon|elastane|spandex|viscose|rayon|wool|linen)\b/i);
    const blendMatch = text.match(/(\d+(?:\.\d+)?)\s*\/\s*\d+(?:\.\d+)?\s*(cotton|polyester|nylon|elastane|spandex|viscose|rayon|wool|linen)\b/i);
    const material = materialMatch || blendMatch;
    if (material) features.push(`${material[1].replace(/\.0$/, '')}% ${material[2].toUpperCase()}`);
    const gsmMatch = text.match(/(\d+(?:\.\d+)?)\s*GSM\b/i);
    if (gsmMatch) features.push(`${gsmMatch[1].replace(/\.0$/, '')} GSM`);
    return features.slice(0, 2);
  }

  function renderFeatures(container, features, pageMode = false) {
    if (!container || !features.length) return;
    const existing = container.querySelector('.zeyravo-product-features');
    if (existing) existing.remove();
    const wrap = document.createElement('div');
    wrap.className = `zeyravo-product-features${pageMode ? ' zeyravo-product-page-features' : ''}`;
    wrap.setAttribute('aria-label', 'Product features');
    features.forEach((feature) => {
      const box = document.createElement('span');
      box.className = 'zeyravo-product-feature';
      box.textContent = feature;
      wrap.appendChild(box);
    });
    container.appendChild(wrap);
  }

  async function getProductFeatures(url) {
    const productPath = new URL(url, window.location.origin).pathname.replace(/\/$/, '') + '.js';
    const response = await fetch(productPath, { credentials:'same-origin', headers:{Accept:'application/json'} });
    if (!response.ok) throw new Error(`Product JSON ${response.status}`);
    const product = await response.json();
    return extractFeatures(product.description);
  }

  function getEmbeddedProductDescription() {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent || '');
        const candidates = Array.isArray(data) ? data : [data];
        for (const item of candidates) {
          if (item && typeof item.description === 'string') return item.description;
          if (item && Array.isArray(item['@graph'])) {
            const graphItem = item['@graph'].find((entry) => entry && typeof entry.description === 'string');
            if (graphItem) return graphItem.description;
          }
        }
      } catch (_) {}
    }
    return '';
  }

  async function enhanceCard(card) {
    if (!card || card.dataset.zeyravoFeaturesReady) return;
    const titleLink = card.querySelector('.card__content .card__information h3 a[href*="/products/"]');
    if (!titleLink) return;
    const title = titleLink.closest('h3');
    if (!title) return;
    card.dataset.zeyravoFeaturesReady = 'loading';
    try {
      const features = await getProductFeatures(titleLink.href);
      if (features.length) renderFeatures(title.parentElement, features);
    } catch (_) {}
    card.dataset.zeyravoFeaturesReady = 'true';
  }

  function observeCards(root = document) {
    const cards = root.querySelectorAll(CARD_SELECTOR);
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) {
      cards.forEach(enhanceCard);
      return;
    }
    if (!cardObserver) {
      cardObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          enhanceCard(entry.target);
        });
      }, { rootMargin:'600px 0px' });
    }
    cards.forEach((card) => {
      if (!card.dataset.zeyravoFeaturesReady) cardObserver.observe(card);
    });
  }

  async function enhanceProductPage() {
    const title = document.querySelector(PRODUCT_TITLE_SELECTOR);
    if (!title || title.dataset.zeyravoFeaturesReady) return;
    title.dataset.zeyravoFeaturesReady = 'loading';
    try {
      let features = extractFeatures(getEmbeddedProductDescription());
      if (!features.length) features = await getProductFeatures(window.location.href);
      if (features.length) {
        const target = title.tagName.toLowerCase() === 'h1' ? title.parentElement : title;
        renderFeatures(target, features, true);
      }
    } catch (_) {}
    title.dataset.zeyravoFeaturesReady = 'true';
  }

  function prefetchNavigation() {
    const connection = navigator.connection;
    if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
    const addPrefetch = (url) => {
      try {
        const parsed = new URL(url, location.href);
        if (parsed.origin !== location.origin || parsed.pathname === location.pathname || prefetched.has(parsed.href)) return;
        prefetched.add(parsed.href);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'document';
        link.href = parsed.href;
        document.head.appendChild(link);
      } catch (_) {}
    };
    document.addEventListener('pointerover', (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
      const href = link.href;
      if (href && href.includes(location.origin)) addPrefetch(href);
    }, { passive:true });
    document.addEventListener('focusin', (event) => {
      const link = event.target.closest?.('a[href]');
      if (link) addPrefetch(link.href);
    }, { passive:true });
  }

  function init() {
    addStyles();
    observeCards();
    enhanceProductPage();
    prefetchNavigation();
    const rescan = () => { observeCards(); enhanceProductPage(); };
    if ('requestIdleCallback' in window) requestIdleCallback(rescan, { timeout:1500 });
    else setTimeout(rescan, 1000);
    const observer = new MutationObserver((mutations) => {
      let added = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          added = true;
          if (node.matches?.(CARD_SELECTOR)) observeCards(node.parentElement || document);
          else if (node.querySelector?.(CARD_SELECTOR)) observeCards(node);
          if (node.matches?.('.product__title') || node.querySelector?.('.product__title')) enhanceProductPage();
        });
      });
      if (added && 'requestIdleCallback' in window) requestIdleCallback(() => observeCards(), { timeout:1000 });
    });
    observer.observe(document.body, { childList:true, subtree:true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once:true });
  else init();
})();